import { useState } from 'react'
import { Download } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { FileUploadDropzone } from '@/components/PlanWizard/FileUploadDropzone'
import type { WarehouseLookupCreatePayload } from '@/types/adminWarehouseLookups'

interface WarehouseLookupBulkImportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  isPending: boolean
  onSubmit: (payload: WarehouseLookupCreatePayload[]) => void
}

function splitCsvLine(line: string): string[] {
  return line
    .split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/)
    .map(cell => cell.trim().replace(/^"|"$/g, ''))
}

function parseBulkInput(raw: string): {
  entries: WarehouseLookupCreatePayload[]
  errors: string[]
} {
  const normalized = raw.replace(/\t/g, ',').trim()
  if (!normalized) {
    return {
      entries: [],
      errors: ['Provide at least one row to import.'],
    }
  }

  const lines = normalized
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean)

  if (!lines.length) {
    return {
      entries: [],
      errors: ['Provide at least one row to import.'],
    }
  }

  const firstColumns = splitCsvLine(lines[0]).map(col => col.toLowerCase())
  const hasHeader =
    firstColumns.includes('lookup_key') || firstColumns.includes('lookup key')

  const dataLines = hasHeader ? lines.slice(1) : lines
  const entries: WarehouseLookupCreatePayload[] = []
  const errors: string[] = []

  dataLines.forEach((line, index) => {
    const row = splitCsvLine(line)
    const rowNumber = hasHeader ? index + 2 : index + 1

    const lookupKey = (row[0] ?? '').trim()
    const valueText = (row[1] ?? '').trim()
    const numericRaw = (row[2] ?? '').trim()

    if (!lookupKey) {
      errors.push(`Row ${rowNumber}: lookup_key is required.`)
      return
    }

    let valueNum: number | null = null
    if (numericRaw) {
      const parsed = Number(numericRaw)
      if (Number.isNaN(parsed)) {
        errors.push(`Row ${rowNumber}: value_num must be numeric.`)
        return
      }
      valueNum = parsed
    }

    entries.push({
      lookup_key: lookupKey,
      value_text: valueText || null,
      value_num: valueNum,
    })
  })

  return { entries, errors }
}

export default function WarehouseLookupBulkImportDialog({
  open,
  onOpenChange,
  isPending,
  onSubmit,
}: WarehouseLookupBulkImportDialogProps) {
  const [file, setFile] = useState<File | null>(null)
  const [parseError, setParseError] = useState<string>('')
  const [parsedEntries, setParsedEntries] = useState<
    WarehouseLookupCreatePayload[]
  >([])
  const [parseErrors, setParseErrors] = useState<string[]>([])

  const handleDownloadSample = () => {
    const sampleCsv = [
      'lookup_key,value_text,value_num',
      'CN_STANDARD,China - Standard,0.0825',
      'US_GROUND_ZONE_1,Ground Zone 1,0.48',
      'OCEAN_EAST,Ocean East Coast,0.15',
    ].join('\n')

    const blob = new Blob([sampleCsv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'warehouse-lookups-sample.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile)
    setParseError('')
    setParseErrors([])
    setParsedEntries([])

    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      setParseError('Please select a .csv file')
      return
    }

    try {
      const text = await selectedFile.text()
      const { entries, errors } = parseBulkInput(text)

      if (errors.length > 0) {
        setParseErrors(errors)
      }
      setParsedEntries(entries)
    } catch (err) {
      setParseError(
        `Failed to read file: ${err instanceof Error ? err.message : 'Unknown error'}`
      )
    }
  }

  const handleSubmit = () => {
    if (parseErrors.length || !parsedEntries.length) {
      return
    }

    onSubmit(parsedEntries)
    setFile(null)
    setParsedEntries([])
    setParseErrors([])
  }

  const handleCancel = () => {
    setFile(null)
    setParsedEntries([])
    setParseErrors([])
    setParseError('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bulk Import Warehouse Lookups</DialogTitle>
          <DialogDescription>
            Upload a CSV file with columns: lookup_key, value_text, value_num.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 overflow-y-auto min-h-0 max-h-[60vh] space-y-4">
          <div>
            <FileUploadDropzone
              onFileSelect={handleFileSelect}
              isLoading={isPending}
              error={parseError}
              accept=".csv"
              maxSizeMB={10}
            />
            <p className="text-[13px] text-[#556374] mt-3">
              <button
                onClick={handleDownloadSample}
                disabled={isPending}
                className="text-[#CF1F2E] hover:underline cursor-pointer inline-flex items-center gap-1 disabled:opacity-60"
              >
                <Download className="h-3 w-3" />
                Download template
              </button>
            </p>
          </div>

          {file && (
            <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 p-3">
              <span className="text-sm text-foreground">{file.name}</span>
              <span className="text-xs text-muted-foreground">
                {parsedEntries.length} parsed entries
              </span>
            </div>
          )}

          {parseErrors.length > 0 ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
              {parseErrors.slice(0, 5).map(error => (
                <p key={error}>{error}</p>
              ))}
              {parseErrors.length > 5 && (
                <p className="mt-1 font-medium">
                  ...and {parseErrors.length - 5} more errors
                </p>
              )}
            </div>
          ) : null}

          {!parseError && !parseErrors.length && parsedEntries.length > 0 ? (
            <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
              ✓ Ready to import {parsedEntries.length} entries.
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={
              isPending || parseErrors.length > 0 || parsedEntries.length === 0
            }
          >
            {isPending ? 'Importing...' : 'Import Entries'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
